import { type AdminUserProvisionInput, adminUserProvisionSchema } from '@atlas/models'
import type { UserRole } from '@atlas/types'
import { Button, HelpText, Input } from '@atlas/ui-primitives'
import { useId, useState } from 'react'
import { DEFAULT_PROVISION_ROLE, ROLE_OPTIONS } from '@/lib/roles'

// Invitation form (feature-local, presentational → storiable). Submits EXACTLY the
// strict `POST /admin/users` contract { email, displayName, role }. Client-side
// validation REUSES the canonical `adminUserProvisionSchema` from @atlas/models — no
// duplicated rules. The browser cannot set cognitoSub / isActive / ids / timestamps;
// only the three approved fields exist. No temporary password is shown or created.

export interface InviteUserFormProps {
	onSubmit: (input: AdminUserProvisionInput) => void
	isPending?: boolean
	/** Safe, pre-formatted submission error (e.g. a 409 reconciliation conflict). */
	errorMessage?: string | null
}

type FieldErrors = Partial<Record<'email' | 'displayName' | 'role', string>>

export function InviteUserForm({ onSubmit, isPending, errorMessage }: InviteUserFormProps) {
	const [email, setEmail] = useState('')
	const [displayName, setDisplayName] = useState('')
	const [role, setRole] = useState<UserRole>(DEFAULT_PROVISION_ROLE)
	const [errors, setErrors] = useState<FieldErrors>({})

	const emailId = useId()
	const nameId = useId()
	const roleId = useId()
	const emailErrId = useId()
	const nameErrId = useId()

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (isPending) return // guard against duplicate submits
		const parsed = adminUserProvisionSchema.safeParse({ email, displayName, role })
		if (!parsed.success) {
			const next: FieldErrors = {}
			for (const issue of parsed.error.issues) {
				const key = issue.path[0]
				if (key === 'email' || key === 'displayName' || key === 'role') next[key] ??= issue.message
			}
			setErrors(next)
			return
		}
		setErrors({})
		onSubmit(parsed.data)
	}

	return (
		<form onSubmit={handleSubmit} className="flex flex-wrap items-start gap-4" noValidate>
			<div className="flex flex-col text-sm">
				<label htmlFor={emailId}>Email</label>
				<Input
					id={emailId}
					type="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					placeholder="person@example.com"
					aria-invalid={!!errors.email}
					aria-describedby={errors.email ? emailErrId : undefined}
				/>
				{errors.email && (
					<HelpText variant="error" id={emailErrId} className="text-xs">
						{errors.email}
					</HelpText>
				)}
			</div>

			<div className="flex flex-col text-sm">
				<label htmlFor={nameId}>Display name</label>
				<Input
					id={nameId}
					value={displayName}
					onChange={(e) => setDisplayName(e.target.value)}
					placeholder="Jane Doe"
					aria-invalid={!!errors.displayName}
					aria-describedby={errors.displayName ? nameErrId : undefined}
				/>
				{errors.displayName && (
					<HelpText variant="error" id={nameErrId} className="text-xs">
						{errors.displayName}
					</HelpText>
				)}
			</div>

			<div className="flex flex-col text-sm">
				<label htmlFor={roleId}>Role</label>
				<select
					id={roleId}
					className="border rounded-ds-base px-2 py-1 h-10"
					value={role}
					onChange={(e) => setRole(e.target.value as UserRole)}
				>
					{ROLE_OPTIONS.map((o) => (
						<option key={o.value} value={o.value}>
							{o.label}
						</option>
					))}
				</select>
			</div>

			<div className="flex flex-col">
				{/* Spacer aligns the button with the inputs (labels add a line above). */}
				<span className="text-sm invisible" aria-hidden="true">
					Submit
				</span>
				<Button type="submit" isDisabled={isPending}>
					{isPending ? 'Inviting…' : 'Invite user'}
				</Button>
			</div>

			{errorMessage && (
				<HelpText variant="error" role="alert" className="mt-0 w-full">
					{errorMessage}
				</HelpText>
			)}

			<p className="w-full text-xs text-ds-text-muted">
				The user receives a Cognito email invitation with a temporary password to complete first-time sign-in. Atlas
				never sets or displays passwords.
			</p>
		</form>
	)
}
